import ToolbarButton from "../toolbar/ToolbarButton";
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAssetTilesUrl, useAssetCounts, useTriggerAssetTilesRegeneration } from "../../api/assets";
import { useImpactedAssetIds } from "../../hooks/useImpactedAssetIds";
import { useFuel3DOptions } from "../../hooks/use3DFuelOptions";
import { use3DLayerActive } from "../../hooks/use3DLayerActive";
import Assets3DBuildings from "./Assets3DBuildings";

const ASSET_DEFAULT_COLOR = "#AF0";
const ASSET_IMPACTED_COLOR = "#ff3b30";
const ASSET_SELECTED_COLOR = "#4a90e2";

/**
 * Build a paint colour expression that highlights impacted assets in red while
 * leaving all other assets in the default colour.
 */
const buildAssetColorExpression = (impactedIds: number[]) => {
  const baseColor =
    impactedIds.length === 0
      ? ASSET_DEFAULT_COLOR
      : [
          "case",
          // Match the asset's `__id` property directly rather than the promoted
          // feature `["id"]`: tippecanoe assigns its own MVT feature ids (it's
          // run without --use-attribute-for-id), so `["id"]` does not resolve to
          // the asset PK. `to-number` guards against `__id` being string-encoded.
          ["in", ["to-number", ["get", "__id"]], ["literal", impactedIds]],
          ASSET_IMPACTED_COLOR,
          ASSET_DEFAULT_COLOR,
        ];
  return [
    "case",
    ["boolean", ["feature-state", "selected"], false],
    ASSET_SELECTED_COLOR,
    baseColor,
  ];
};

const AssetsLayer = (
  id: string,
  name: string
) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [active, setActive] = useState(true);
  const [stale, setStale] = useState(false);
  const [polling, setPolling] = useState(false);
  const regenerationTriggeredRef = useRef(false);
  const prevTilesUrlRef = useRef<string | undefined>(undefined);

  const { mutate: triggerRegeneration } = useTriggerAssetTilesRegeneration(projectId);
  const { data: assetCounts } = useAssetCounts(projectId);
  const { data: tilesData } = useAssetTilesUrl(projectId, polling);
  const impactedAssetIds = useImpactedAssetIds();
  const prevImpactedKeyRef = useRef<string>("");
  const { buildings: showBuildings } = useFuel3DOptions();
  const is3DLayerActive = use3DLayerActive();

  const tilesUrl = (tilesData as any)?.data?.tiles_url as string | undefined;
  const totalAssets = (assetCounts as any)?.data?.total ?? 0;

  const prevAssetCountRef = useRef<number>(totalAssets);

  // Auto-regeneration: trigger if tiles URL missing but assets exist
  useEffect(() => {
    if (!regenerationTriggeredRef.current && !tilesUrl && totalAssets > 0) {
      regenerationTriggeredRef.current = true;
      triggerRegeneration();
      setPolling(true);
    }
  }, [tilesUrl, totalAssets, triggerRegeneration]);

  // Start polling when asset count changes (mutation happened, tiles regenerating)
  useEffect(() => {
    if (prevAssetCountRef.current !== totalAssets && totalAssets >= 0) {
      setPolling(true);
    }
    prevAssetCountRef.current = totalAssets;
  }, [totalAssets]);

  // When tiles URL changes, stop polling and mark stale for re-render
  useEffect(() => {
    if (tilesUrl && tilesUrl !== prevTilesUrlRef.current) {
      setPolling(false);
      regenerationTriggeredRef.current = false;
      setStale(true);
    }
    prevTilesUrlRef.current = tilesUrl;
  }, [tilesUrl]);

  // Re-apply paint properties whenever the impacted asset set changes
  useEffect(() => {
    const key = impactedAssetIds.slice().sort((a, b) => a - b).join(",");
    if (key !== prevImpactedKeyRef.current) {
      prevImpactedKeyRef.current = key;
      setStale(true);
    }
  }, [impactedAssetIds]);

  // Re-apply paint when the Buildings 3D toggle or the 3D Layers toggle changes
  // so the footprint fill dims under (or restores from under) the extruded
  // buildings — buildings are only extruded while the 3D Layers toggle is on.
  useEffect(() => {
    setStale(true);
  }, [showBuildings, is3DLayerActive]);

  const vectorSource = tilesUrl
    ? {
        tiles: [tilesUrl],
        minzoom: 5,
        // MUST match the backend's PMTILES_MAX_ZOOM (api/shared/s3_tiles.py) that
        // the asset tiles are generated to. If this is higher than the real max,
        // mapbox requests non-existent z>max tiles instead of overzooming the
        // top tile, so assets vanish once you zoom past that level (and stay
        // blank after an edit clears the tile cache). Assets are generated to
        // z12, so overzoom from 12.
        maxzoom: 12,
        tileSize: 512,
        promoteId: "__id",
      }
    : null;

  // Buildings extrude to 3D only while the "3D Layers" toggle is on AND
  // Buildings is selected in that layer's options. With the 3D Layers toggle
  // off, buildings stay 2D footprints even if the Buildings option is on.
  // The 3D layer is attached whenever the 3D Layers toggle is on (so the
  // geometry is ready); the Buildings option just flips its visibility.
  const buildings3DAttached = is3DLayerActive && !!vectorSource;
  const show3DBuildings = buildings3DAttached && showBuildings;

  const handleToggle = () => {
    const nextActive = !active;
    setActive(nextActive);
    if (nextActive) {
      setStale(true);
    }
  };

  const fillColorExpression = buildAssetColorExpression(impactedAssetIds);

  const layerStyle = {
    id: id,
    type: "fill" as const,
    "source-layer": "assets",
    paint: {
      "fill-color": fillColorExpression,
      // When Building assets are extruded in 3D, mute just their 2D footprint
      // fill (the bright slab would bleed out from under the walls); when they
      // are not extruded they show as normal flat footprints, and roads, lines
      // and other asset types always keep their normal fill.
      "fill-opacity": show3DBuildings
        ? ["case", ["==", ["get", "asset_type"], "Building"], 0.15, 0.5]
        : 0.5,
      "fill-outline-color": fillColorExpression,
    },
    layout: {
      visibility: active ? "visible" : "none",
    },
  };

  const toolbarButton = (
    <ToolbarButton
      key={id}
      id={id}
      name={name}
      toggledOn={active}
      enabled={!!vectorSource}
      onClick={handleToggle}
      icon={"house"}
    />
  );

  const computedActive = active && !!vectorSource;

  return {
    id: id,
    name: name,
    type: "vector" as const,
    source: vectorSource,
    rawSource: vectorSource,
    layerStyle: [layerStyle],
    toolbarButton: toolbarButton,
    active: computedActive,
    stale: stale,
    setStale: setStale,
    // 3D extruded buildings — attached while the "3D Layers" toggle is on; the
    // Buildings option toggles their visibility on the live layer (off → they
    // stay 2D footprints), so it flips instantly without a re-attach.
    customComponent: (
      <Assets3DBuildings
        sourceId={id}
        active={buildings3DAttached}
        visible={showBuildings}
      />
    ),
  };
};

export default AssetsLayer;